import React, { useRef, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import './CameraSelector.css';

interface CameraSelectorProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CameraSelector: React.FC<CameraSelectorProps> = ({ isOpen, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [sortBy, setSortBy] = useState('distance');
  const [filters, setFilters] = useState({
    distanceRange: true,
    angleRange: false,
    fullView: true,
    highQuality: false,
    bestSimilarity: false
  });

  // Close menu on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const menuContent = (
    <>
      {/* Backdrop overlay */}
      <div className="camera-selector-backdrop" onClick={onClose} />

      {/* Modal menu */}
      <div ref={menuRef} className="camera-selector-menu">
        {/* Header with close button */}
        <div className="camera-selector-header">
          <h3>Camera Selector</h3>
          <button className="camera-selector-close-btn" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {/* Sort Section */}
      <div className="camera-selector-section">
        <div className="camera-selector-section-title">Sort By</div>
        <div className="camera-selector-options">
          <label className="camera-selector-radio">
            <input 
              type="radio" 
              name="sort" 
              value="distance" 
              checked={sortBy === 'distance'}
              onChange={(e) => setSortBy(e.target.value)}
              onTouchEnd={() => setSortBy('distance')}
            />
            <span>Distance</span>
          </label>
          <label className="camera-selector-radio">
            <input 
              type="radio" 
              name="sort" 
              value="angle" 
              checked={sortBy === 'angle'}
              onChange={(e) => setSortBy(e.target.value)}
              onTouchEnd={() => setSortBy('angle')}
            />
            <span>View Angle</span>
          </label>
          <label className="camera-selector-radio">
            <input 
              type="radio" 
              name="sort" 
              value="around-point" 
              checked={sortBy === 'around-point'}
              onChange={(e) => setSortBy(e.target.value)}
              onTouchEnd={() => setSortBy('around-point')}
            />
            <span>Around Point</span>
          </label>
          <label className="camera-selector-radio">
            <input 
              type="radio" 
              name="sort" 
              value="incident" 
              checked={sortBy === 'incident'}
              onChange={(e) => setSortBy(e.target.value)}
              onTouchEnd={() => setSortBy('incident')}
            />
            <span>Incident Angle</span>
          </label>
          <label className="camera-selector-radio">
            <input 
              type="radio" 
              name="sort" 
              value="quality" 
              checked={sortBy === 'quality'}
              onChange={(e) => setSortBy(e.target.value)}
              onTouchEnd={() => setSortBy('quality')}
            />
            <span>Quality</span>
          </label>
        </div>
      </div>

      {/* Divider */}
      <div className="camera-selector-divider"></div>

      {/* Filter Section */}
      <div className="camera-selector-section">
        <div className="camera-selector-section-title">Filter</div>
        <div className="camera-selector-options">
          <label className="camera-selector-checkbox">
            <input 
              type="checkbox" 
              checked={filters.distanceRange}
              onChange={(e) => setFilters({ ...filters, distanceRange: e.target.checked })}
              onTouchEnd={() => setFilters({ ...filters, distanceRange: !filters.distanceRange })}
            />
            <span>Distance Range</span>
          </label>
          <label className="camera-selector-checkbox">
            <input 
              type="checkbox" 
              checked={filters.angleRange}
              onChange={(e) => setFilters({ ...filters, angleRange: e.target.checked })}
              onTouchEnd={() => setFilters({ ...filters, angleRange: !filters.angleRange })}
            />
            <span>Angle Range</span>
          </label>
          <label className="camera-selector-checkbox">
            <input 
              type="checkbox" 
              checked={filters.fullView}
              onChange={(e) => setFilters({ ...filters, fullView: e.target.checked })}
              onTouchEnd={() => setFilters({ ...filters, fullView: !filters.fullView })}
            />
            <span>Full View</span>
          </label>
          <label className="camera-selector-checkbox">
            <input 
              type="checkbox" 
              checked={filters.highQuality}
              onChange={(e) => setFilters({ ...filters, highQuality: e.target.checked })}
              onTouchEnd={() => setFilters({ ...filters, highQuality: !filters.highQuality })}
            />
            <span>High Quality</span>
          </label>
          <label className="camera-selector-checkbox">
            <input 
              type="checkbox" 
              checked={filters.bestSimilarity}
              onChange={(e) => setFilters({ ...filters, bestSimilarity: e.target.checked })}
              onTouchEnd={() => setFilters({ ...filters, bestSimilarity: !filters.bestSimilarity })}
            />
            <span>Best Similarity</span>
          </label>
        </div>
      </div>
      </div>
    </>
  );

  return ReactDOM.createPortal(menuContent, document.body);
};

